// AuthContext — single source of truth for the logged-in user.
// Reads cookie session on mount, exposes login / register / google / logout.

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { clearAllLocalUserData } from './testResults';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All auth + protected calls send the httpOnly cookies.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// Track the last known user_id so we can detect identity changes and purge
// the previous user's locally cached quiz / test / chat data.
let _lastUserId = undefined;
function rememberUser(u) {
  const next = u && u !== false ? (u.user_id || u.email || null) : null;
  if (_lastUserId !== undefined && _lastUserId !== next) {
    clearAllLocalUserData();
  }
  _lastUserId = next;
  try { window.__learnixta_user_id = next || 'anon'; } catch { /* ignore */ }
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking, object = authed, false = anonymous
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    // Skip /me check if we're mid-OAuth callback — AuthCallback handles it.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      rememberUser(data.user);
      setUser(data.user);
    } catch {
      rememberUser(false);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    rememberUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name, track_id) => {
    const payload = { email, password, name };
    if (track_id) payload.track_id = track_id;
    const { data } = await axios.post(`${API}/auth/register`, payload);
    rememberUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const googleExchange = useCallback(async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId });
    rememberUser(data.user);
    setUser(data.user);
    return { ...data.user, return_to: data.return_to };
  }, []);

  const googleStart = useCallback(async (next = '/dashboard') => {
    const { data } = await axios.get(`${API}/auth/google/start`, { params: { next } });
    window.location.assign(data.url);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // ignore
    }
    clearAllLocalUserData();
    rememberUser(false);
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, googleStart, googleExchange, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// Helper that grabs whatever is in localStorage and uploads it.
async function syncLocalToServer() {
  try {
    const raw = localStorage.getItem('learnixta_test_results');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.tests || Object.keys(parsed.tests).length === 0) return;
    await axios.post(`${API}/test-results/bulk`, {
      pathway: parsed.pathway || null,
      tests: parsed.tests || {},
      completedAt: parsed.completedAt || null,
    });
  } catch {
    // non-fatal — keep local copy
  }
}
